type JwtClaims = Record<string, unknown>;

const isBrowser = () => typeof window !== 'undefined';

const base64UrlDecode = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');

  if (!isBrowser() || typeof window.atob !== 'function') return '';

  const binary = window.atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const isLikelyJwt = (value?: string | null): boolean => {
  const token = typeof value === 'string' ? value.trim() : '';
  return token.split('.').length === 3 && token.length > 80;
};

export const decodeJwtClaims = (token?: string | null): JwtClaims | null => {
  const safeToken = typeof token === 'string' ? token.trim() : '';
  if (!isLikelyJwt(safeToken)) return null;

  try {
    const [, payload] = safeToken.split('.');
    return JSON.parse(base64UrlDecode(payload)) as JwtClaims;
  } catch {
    return null;
  }
};

export const getUserDisplayNameFromToken = (token?: string | null): string => {
  const claims = decodeJwtClaims(token);
  if (!claims) return '';

  const candidates = [
    claims.email,
    claims.name,
    claims.preferred_username,
    claims['cognito:username'],
    claims.sub,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return '';
};

export const resolveUserDisplayName = (
  username?: string | null,
  token?: string | null,
  fallback = 'Mi cuenta'
): string => {
  const safeUsername = typeof username === 'string' ? username.trim() : '';
  if (safeUsername && !isLikelyJwt(safeUsername)) return safeUsername;

  const fromUsernameToken = getUserDisplayNameFromToken(safeUsername);
  if (fromUsernameToken) return fromUsernameToken;

  const fromToken = getUserDisplayNameFromToken(token);
  if (fromToken) return fromToken;

  return fallback;
};
